/**
 * SettingsPage Component
 * SESSION_201: Post-onboarding settings page
 * SESSION_205: Redesigned with polished UI and simplified address correction link
 *
 * Allows users to edit their business info and contact preferences
 * after completing onboarding.
 *
 * Sections:
 * - Business Information (with address correction link)
 * - Notification Preferences
 */

import React, { useCallback, useState } from 'react';
import {
  Title,
  Text,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Box,
  Alert,
  Switch,
  Loader,
  Center,
  ThemeIcon,
  Card,
  SimpleGrid
} from '@mantine/core';
import {
  IconSettings,
  IconBuilding,
  IconPhone,
  IconMail,
  IconWorld,
  IconMapPin,
  IconMessageCircle,
  IconInfoCircle,
  IconBell,
  IconCheck,
  IconPencil,
  IconX
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useOnboardingStatusQuery, useUpdateBusinessByOwnerMutation, useUpdateContactPreferencesMutation } from '../../../application/queries/onboardingQueries';
import { AddressCorrectionModal } from '../../components/onboarding/AddressCorrectionModal';
import type { UpdateBusinessByOwnerRequest, UpdateContactPreferencesRequest } from '../../../domain/entities/Onboarding';
import {
  isValidPhoneNumber,
  formatPhoneAsYouType,
  normalizePhoneNumber,
  formatPhoneForDisplay
} from '../../../utils/phoneValidation';

/**
 * Validates email format
 */
const isValidEmail = (email: string): boolean => {
  if (!email.trim()) return true;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates URL format
 */
const isValidUrl = (url: string): boolean => {
  if (!url.trim()) return true;
  try {
    const urlToTest = url.startsWith('http') ? url : `https://${url}`;
    new URL(urlToTest);
    return true;
  } catch {
    return false;
  }
};

/**
 * SettingsPage - User settings page for editing business and contact preferences
 */
export const SettingsPage: React.FC = (): React.ReactElement => {
  // Fetch current data
  const { data: onboardingStatus, isLoading: isLoadingStatus } = useOnboardingStatusQuery(true);

  // Mutations
  const updateBusinessMutation = useUpdateBusinessByOwnerMutation();
  const updateContactMutation = useUpdateContactPreferencesMutation();

  // Address correction modal state
  const [showAddressCorrectionModal, setShowAddressCorrectionModal] = useState(false);

  // Business info form state
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [businessErrors, setBusinessErrors] = useState<Record<string, string>>({});
  const [businessInitialized, setBusinessInitialized] = useState(false);

  // SESSION_206: Edit mode state for business info
  const [isEditingBusinessInfo, setIsEditingBusinessInfo] = useState(false);

  // Contact preferences form state
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [smsNotificationsEnabled, setSmsNotificationsEnabled] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [contactInitialized, setContactInitialized] = useState(false);

  // Initialize form values when data loads
  React.useEffect(() => {
    if (onboardingStatus && !businessInitialized) {
      setBusinessPhone(formatPhoneForDisplay(onboardingStatus.business?.phone) ?? '');
      setBusinessEmail(onboardingStatus.business?.email ?? '');
      setBusinessWebsite(onboardingStatus.business?.website ?? '');
      setBusinessDescription(onboardingStatus.business?.description ?? '');
      setBusinessInitialized(true);
    }
  }, [onboardingStatus, businessInitialized]);

  React.useEffect(() => {
    if (onboardingStatus && !contactInitialized) {
      setEmailNotificationsEnabled(onboardingStatus.emailNotificationsEnabled);
      setSmsNotificationsEnabled(onboardingStatus.smsNotificationsEnabled);
      setPhoneNumber(onboardingStatus.phoneNumber ? formatPhoneForDisplay(onboardingStatus.phoneNumber) : '');
      setContactInitialized(true);
    }
  }, [onboardingStatus, contactInitialized]);

  /**
   * Handle business info save
   */
  const handleSaveBusinessInfo = useCallback((): void => {
    const newErrors: Record<string, string> = {};

    if (!isValidEmail(businessEmail)) {
      newErrors['email'] = 'Please enter a valid email address';
    }
    if (!isValidUrl(businessWebsite)) {
      newErrors['website'] = 'Please enter a valid website URL';
    }

    setBusinessErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    if (!onboardingStatus?.business?.id) {
      return;
    }

    const request: UpdateBusinessByOwnerRequest = {
      phone: businessPhone.trim() || null,
      email: businessEmail.trim() || null,
      website: businessWebsite.trim() || null,
      description: businessDescription.trim() || null
    };

    updateBusinessMutation.mutate(
      {
        businessId: onboardingStatus.business.id,
        request
      },
      {
        onSuccess: () => {
          // SESSION_206: Exit edit mode on successful save
          setIsEditingBusinessInfo(false);
          notifications.show({
            title: 'Success',
            message: 'Business information saved successfully',
            color: 'green',
            icon: <IconCheck size={16} />
          });
        }
      }
    );
  }, [businessPhone, businessEmail, businessWebsite, businessDescription, onboardingStatus, updateBusinessMutation]);

  /**
   * Handle cancel business info editing - SESSION_206
   * Resets form to original values and exits edit mode
   */
  const handleCancelBusinessInfo = useCallback((): void => {
    if (onboardingStatus?.business) {
      setBusinessPhone(formatPhoneForDisplay(onboardingStatus.business.phone) ?? '');
      setBusinessEmail(onboardingStatus.business.email ?? '');
      setBusinessWebsite(onboardingStatus.business.website ?? '');
      setBusinessDescription(onboardingStatus.business.description ?? '');
    }
    setBusinessErrors({});
    setIsEditingBusinessInfo(false);
  }, [onboardingStatus]);

  /**
   * Handle contact preferences save
   */
  const handleSaveContactPreferences = useCallback((): void => {
    // Validate phone if SMS is enabled
    if (smsNotificationsEnabled && !phoneNumber.trim()) {
      setPhoneError('Phone number is required when SMS notifications are enabled');
      return;
    }

    if (phoneNumber.trim() && !isValidPhoneNumber(phoneNumber)) {
      setPhoneError('Please enter a valid US phone number');
      return;
    }

    setPhoneError(undefined);

    const normalizedPhone = phoneNumber.trim() ? normalizePhoneNumber(phoneNumber) : null;

    const request: UpdateContactPreferencesRequest = {
      emailNotificationsEnabled,
      smsNotificationsEnabled,
      phoneNumber: normalizedPhone
    };

    updateContactMutation.mutate(request, {
      onSuccess: () => {
        notifications.show({
          title: 'Success',
          message: 'Contact preferences saved successfully',
          color: 'green',
          icon: <IconCheck size={16} />
        });
      }
    });
  }, [emailNotificationsEnabled, smsNotificationsEnabled, phoneNumber, updateContactMutation]);

  /**
   * Handle phone number change with auto-formatting (user phone for SMS)
   */
  const handlePhoneChange = useCallback((value: string): void => {
    const formatted = formatPhoneAsYouType(value);
    setPhoneNumber(formatted);
    if (phoneError) {
      setPhoneError(undefined);
    }
  }, [phoneError]);

  /**
   * Handle business phone number change with auto-formatting
   */
  const handleBusinessPhoneChange = useCallback((value: string): void => {
    const formatted = formatPhoneAsYouType(value);
    setBusinessPhone(formatted);
  }, []);

  // Show loading state
  if (isLoadingStatus) {
    return (
      <Box py="xl">
        <Center h={300}>
          <Loader size="lg" color="indigo" />
        </Center>
      </Box>
    );
  }

  // Show error if no business data
  if (!onboardingStatus?.business) {
    return (
      <Box py="xl">
        <Alert color="yellow" icon={<IconInfoCircle size={16} />} radius="md">
          No business information found. Please complete onboarding first.
        </Alert>
      </Box>
    );
  }

  const business = onboardingStatus.business;

  return (
    <>
      {/* Address Correction Modal */}
      <AddressCorrectionModal
        opened={showAddressCorrectionModal}
        onClose={() => setShowAddressCorrectionModal(false)}
        businessId={business.id}
        businessName={business.name}
        currentAddress={business.address ?? ''}
      />

      <Box py="xl">
        <Stack gap="xl">
          {/* Page Header */}
          <Paper
            p="xl"
            radius="lg"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.15)'
            }}
          >
            <Group gap="lg">
              <ThemeIcon
                size={56}
                radius="xl"
                variant="gradient"
                gradient={{ from: 'indigo', to: 'violet', deg: 135 }}
              >
                <IconSettings size={28} />
              </ThemeIcon>
              <Box>
                <Title order={2} fw={700} style={{ letterSpacing: '-0.02em' }}>
                  Settings
                </Title>
                <Text c="dimmed" size="sm" mt={4}>
                  Manage your business information and notification preferences
                </Text>
              </Box>
            </Group>
          </Paper>

          {/* Business Information Section */}
          <Card withBorder radius="lg" padding={0} shadow="sm">
            {/* Card Header - SESSION_206: Added edit toggle */}
            <Box
              p="lg"
              style={{
                borderBottom: '1px solid var(--mantine-color-gray-2)',
                background: 'linear-gradient(180deg, var(--mantine-color-gray-0) 0%, transparent 100%)'
              }}
            >
              <Group justify="space-between">
                <Group gap="sm">
                  <ThemeIcon variant="light" color="blue" size="lg" radius="md">
                    <IconBuilding size={20} />
                  </ThemeIcon>
                  <Title order={3} fw={600}>Business Information</Title>
                </Group>
                {!isEditingBusinessInfo && (
                  <Button
                    variant="subtle"
                    size="sm"
                    leftSection={<IconPencil size={14} />}
                    onClick={() => setIsEditingBusinessInfo(true)}
                  >
                    Edit Info
                  </Button>
                )}
              </Group>
            </Box>

            <Stack gap={0}>
              {/* Business Details Section */}
              <Box
                p="lg"
                style={{
                  background: 'rgba(248, 250, 252, 0.5)',
                  borderBottom: '1px solid var(--mantine-color-gray-2)'
                }}
              >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                  {/* Business Name */}
                  <Box>
                    <Group gap="sm" mb={6}>
                      <IconBuilding size={16} color="var(--mantine-color-gray-5)" />
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Business Name
                      </Text>
                    </Group>
                    <Text fw={500} size="md">{business.name}</Text>
                  </Box>

                  {/* Address */}
                  <Box>
                    <Group gap="sm" mb={6}>
                      <IconMapPin size={16} color="var(--mantine-color-gray-5)" />
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Address
                      </Text>
                    </Group>
                    <Text fw={500} size="md" style={{ whiteSpace: 'pre-line' }}>
                      {business.address || 'No address on file'}
                    </Text>
                    <Text
                      size="xs"
                      c="dimmed"
                      mt="xs"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowAddressCorrectionModal(true)}
                      td="underline"
                    >
                      Submit a correction request
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Contact Information Section - SESSION_206: Conditional edit/read mode */}
              <Box p="lg">
                {isEditingBusinessInfo ? (
                  /* Edit Mode - Show form inputs */
                  <>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <TextInput
                        label="Phone Number"
                        placeholder="(555) 123-4567"
                        leftSection={<IconPhone size={16} />}
                        value={businessPhone}
                        onChange={(e) => handleBusinessPhoneChange(e.currentTarget.value)}
                        disabled={updateBusinessMutation.isPending}
                        radius="md"
                      />

                      <TextInput
                        label="Email"
                        placeholder="contact@business.com"
                        leftSection={<IconMail size={16} />}
                        value={businessEmail}
                        onChange={(e) => setBusinessEmail(e.currentTarget.value)}
                        error={businessErrors['email']}
                        disabled={updateBusinessMutation.isPending}
                        radius="md"
                      />
                    </SimpleGrid>

                    <TextInput
                      label="Website"
                      placeholder="www.business.com"
                      leftSection={<IconWorld size={16} />}
                      value={businessWebsite}
                      onChange={(e) => setBusinessWebsite(e.currentTarget.value)}
                      error={businessErrors['website']}
                      disabled={updateBusinessMutation.isPending}
                      mt="md"
                      radius="md"
                    />

                    <Textarea
                      label="Description"
                      placeholder="Tell customers about your business..."
                      minRows={3}
                      maxRows={6}
                      value={businessDescription}
                      onChange={(e) => setBusinessDescription(e.currentTarget.value)}
                      disabled={updateBusinessMutation.isPending}
                      mt="md"
                      radius="md"
                    />

                    <Group justify="flex-end" mt="xl">
                      <Button
                        variant="default"
                        onClick={handleCancelBusinessInfo}
                        disabled={updateBusinessMutation.isPending}
                        leftSection={<IconX size={16} />}
                        radius="md"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveBusinessInfo}
                        loading={updateBusinessMutation.isPending}
                        leftSection={<IconCheck size={16} />}
                        radius="md"
                        variant="filled"
                        color="indigo"
                      >
                        Save Business Info
                      </Button>
                    </Group>
                  </>
                ) : (
                  /* Read Mode - Show text display */
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                    <Box>
                      <Group gap="sm" mb={6}>
                        <IconPhone size={16} color="var(--mantine-color-gray-5)" />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Phone Number
                        </Text>
                      </Group>
                      <Text fw={500} size="md">
                        {businessPhone || <Text span c="dimmed" fs="italic">Not set</Text>}
                      </Text>
                    </Box>

                    <Box>
                      <Group gap="sm" mb={6}>
                        <IconMail size={16} color="var(--mantine-color-gray-5)" />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Email
                        </Text>
                      </Group>
                      <Text fw={500} size="md">
                        {businessEmail || <Text span c="dimmed" fs="italic">Not set</Text>}
                      </Text>
                    </Box>

                    <Box>
                      <Group gap="sm" mb={6}>
                        <IconWorld size={16} color="var(--mantine-color-gray-5)" />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Website
                        </Text>
                      </Group>
                      <Text fw={500} size="md">
                        {businessWebsite || <Text span c="dimmed" fs="italic">Not set</Text>}
                      </Text>
                    </Box>

                    <Box>
                      <Group gap="sm" mb={6}>
                        <IconInfoCircle size={16} color="var(--mantine-color-gray-5)" />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Description
                        </Text>
                      </Group>
                      <Text fw={500} size="md" style={{ whiteSpace: 'pre-wrap' }}>
                        {businessDescription || <Text span c="dimmed" fs="italic">No description</Text>}
                      </Text>
                    </Box>
                  </SimpleGrid>
                )}
              </Box>
            </Stack>
          </Card>

          {/* Notification Preferences Section */}
          <Card withBorder radius="lg" padding={0} shadow="sm">
            {/* Card Header */}
            <Box
              p="lg"
              style={{
                borderBottom: '1px solid var(--mantine-color-gray-2)',
                background: 'linear-gradient(180deg, var(--mantine-color-gray-0) 0%, transparent 100%)'
              }}
            >
              <Group gap="sm">
                <ThemeIcon variant="light" color="violet" size="lg" radius="md">
                  <IconBell size={20} />
                </ThemeIcon>
                <Title order={3} fw={600}>Notification Preferences</Title>
              </Group>
            </Box>

            <Box p="lg">
              <Alert
                icon={<IconInfoCircle size={16} />}
                color="indigo"
                variant="light"
                radius="md"
                mb="lg"
              >
                Choose how you'd like to be notified when your business is mentioned online.
              </Alert>

              <Stack gap="lg">
                {/* Email Notifications */}
                <Paper
                  p="md"
                  radius="md"
                  withBorder
                  style={{
                    borderColor: emailNotificationsEnabled
                      ? 'var(--mantine-color-indigo-3)'
                      : 'var(--mantine-color-gray-3)',
                    background: emailNotificationsEnabled
                      ? 'rgba(99, 102, 241, 0.03)'
                      : 'transparent',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="md">
                      <ThemeIcon
                        variant={emailNotificationsEnabled ? 'filled' : 'light'}
                        color={emailNotificationsEnabled ? 'indigo' : 'gray'}
                        size="lg"
                        radius="md"
                        style={{ transition: 'all 0.2s ease' }}
                      >
                        <IconMail size={18} />
                      </ThemeIcon>
                      <Box>
                        <Text size="sm" fw={600}>Email Notifications</Text>
                        <Text size="xs" c="dimmed">
                          Receive email alerts for new mentions
                        </Text>
                      </Box>
                    </Group>
                    <Switch
                      checked={emailNotificationsEnabled}
                      onChange={(e) => setEmailNotificationsEnabled(e.currentTarget.checked)}
                      disabled={updateContactMutation.isPending}
                      size="lg"
                      color="indigo"
                    />
                  </Group>
                </Paper>

                {/* SMS Notifications */}
                <Paper
                  p="md"
                  radius="md"
                  withBorder
                  style={{
                    borderColor: smsNotificationsEnabled
                      ? 'var(--mantine-color-violet-3)'
                      : 'var(--mantine-color-gray-3)',
                    background: smsNotificationsEnabled
                      ? 'rgba(139, 92, 246, 0.03)'
                      : 'transparent',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="md">
                      <ThemeIcon
                        variant={smsNotificationsEnabled ? 'filled' : 'light'}
                        color={smsNotificationsEnabled ? 'violet' : 'gray'}
                        size="lg"
                        radius="md"
                        style={{ transition: 'all 0.2s ease' }}
                      >
                        <IconMessageCircle size={18} />
                      </ThemeIcon>
                      <Box>
                        <Text size="sm" fw={600}>SMS Notifications</Text>
                        <Text size="xs" c="dimmed">
                          Receive text message alerts for new mentions
                        </Text>
                      </Box>
                    </Group>
                    <Switch
                      checked={smsNotificationsEnabled}
                      onChange={(e) => setSmsNotificationsEnabled(e.currentTarget.checked)}
                      disabled={updateContactMutation.isPending}
                      size="lg"
                      color="violet"
                    />
                  </Group>

                  {/* Phone Number Input (shown when SMS enabled) */}
                  {smsNotificationsEnabled && (
                    <Box mt="md" pt="md" style={{ borderTop: '1px dashed var(--mantine-color-gray-3)' }}>
                      <TextInput
                        label="Phone Number for SMS"
                        placeholder="(206) 555-1234"
                        description="US phone numbers only. Required for SMS notifications."
                        leftSection={<IconPhone size={16} />}
                        value={phoneNumber}
                        onChange={(e) => handlePhoneChange(e.currentTarget.value)}
                        error={phoneError}
                        disabled={updateContactMutation.isPending}
                        required
                        radius="md"
                      />
                    </Box>
                  )}
                </Paper>
              </Stack>

              <Group justify="flex-end" mt="xl">
                <Button
                  onClick={handleSaveContactPreferences}
                  loading={updateContactMutation.isPending}
                  leftSection={<IconCheck size={16} />}
                  radius="md"
                  variant="filled"
                  color="violet"
                >
                  Save Preferences
                </Button>
              </Group>
            </Box>
          </Card>
        </Stack>
      </Box>
    </>
  );
};
